/**
 * The game rules, as pure functions.
 *
 * Per `CLAUDE.md` and docs/02 this module is imported by **both** client and
 * server. The server calls it to authorise actions; the client calls it to grey
 * out what you can't afford and preview what a claim will cost. The rules are
 * never forked between the two — a divergence here is a desync by construction.
 *
 * Everything in this file is a pure function of its arguments. No I/O, no
 * mutation of inputs, no reads of global state. That is what makes the numbers
 * testable in isolation, and it is why the combat math below can be tuned
 * against thousands of headless sessions without a renderer existing.
 */
import { ADJACENT_SUPPORT_FACTOR, AETHER_STORAGE_CAP, ATTRITION_RATE, BASE_POPULATION_CAP, BASE_STORAGE_CAP, BIOME_YIELDS, BUILDINGS, CAPTURE_DECAY_RATE, CAPTURE_RATE, EXPANSION_COSTS, EXPANSION_SCALING_PER_HEX, FORTIFY_DEFENCE_MULTIPLIER, GLORY_PER_BANKED_RESOURCE, GLORY_PER_HEX, GLORY_PER_SPECIAL_HEX, GOD_TOOLS_DEFINITIONS, GLORY_PER_TRADE_VOLUME, MAX_WORKER_EFFICIENCY, POPULATION_PER_FOOD_RATE, SPECIAL_HEX_YIELDS, STORAGE_CAP_PER_HEX, SURGE_WEIGHT, TERRAIN_DEFENCE, WORKERS_PER_HEX, } from "./balance.js";
import { hexKey, neighbors } from "./hexGrid.js";
import { RESOURCE_TYPES } from "./messages.js";
// ---------------------------------------------------------------------------
// Resource bundle arithmetic
// ---------------------------------------------------------------------------
export function emptyBundle() {
    return { food: 0, timber: 0, ore: 0, gold: 0, aether: 0 };
}
export function cloneBundle(bundle) {
    return {
        food: bundle.food,
        timber: bundle.timber,
        ore: bundle.ore,
        gold: bundle.gold,
        aether: bundle.aether,
    };
}
export function addBundles(a, b) {
    return {
        food: a.food + b.food,
        timber: a.timber + b.timber,
        ore: a.ore + b.ore,
        gold: a.gold + b.gold,
        aether: a.aether + b.aether,
    };
}
export function subtractBundles(a, b) {
    return {
        food: a.food - b.food,
        timber: a.timber - b.timber,
        ore: a.ore - b.ore,
        gold: a.gold - b.gold,
        aether: a.aether - b.aether,
    };
}
export function scaleBundle(bundle, factor) {
    return {
        food: bundle.food * factor,
        timber: bundle.timber * factor,
        ore: bundle.ore * factor,
        gold: bundle.gold * factor,
        aether: bundle.aether * factor,
    };
}
export function bundleTotal(bundle) {
    return bundle.food + bundle.timber + bundle.ore + bundle.gold + bundle.aether;
}
/** True if `available` covers every line of `cost`. */
export function canAfford(available, cost) {
    return RESOURCE_TYPES.every((type) => available[type] >= cost[type]);
}
/** The resource types `available` falls short on, for UI feedback. */
export function missingResources(available, cost) {
    return RESOURCE_TYPES.filter((type) => available[type] < cost[type]);
}
export function clampBundleToCap(bundle, cap) {
    return {
        food: Math.min(bundle.food, cap),
        timber: Math.min(bundle.timber, cap),
        ore: Math.min(bundle.ore, cap),
        gold: Math.min(bundle.gold, cap),
        aether: Math.min(bundle.aether, AETHER_STORAGE_CAP),
    };
}
/**
 * A kingdom's cap for the four terrain resources.
 *
 * `storageBonus` is the sum of `buildingStorageBonus` over everything the kingdom
 * holds. Summing is the caller's job — it is a fold over whatever collection that
 * side already has (hex records on the server, `HexState`s on the client) — while
 * the per-building number stays in `BUILDINGS`, so there is still exactly one
 * place the value lives.
 */
export function storageCap(territoryCount, storageBonus = 0) {
    return BASE_STORAGE_CAP + territoryCount * STORAGE_CAP_PER_HEX + Math.max(0, storageBonus);
}
/**
 * The cap for **one** resource. Aether has its own flat ceiling; the other four
 * scale with territory.
 *
 * This exists because the rule was briefly implemented in three places — the
 * simulation's per-tick clamp, its end-of-tick settlement pass, and
 * `clampBundleToCap` — and only the third one knew about Aether, so the
 * simulation happily banked 55 of a resource capped at 40. Anything clamping a
 * store must ask this rather than reach for `storageCap` directly.
 */
export function resourceCap(type, territoryCount, storageBonus = 0) {
    // Storehouses deliberately do not raise the Aether ceiling: that cap is flat so
    // Aether stays a resource you spend rather than bank, and letting a building
    // lift it would undo the reason it exists.
    return type === "aether" ? AETHER_STORAGE_CAP : storageCap(territoryCount, storageBonus);
}
// ---------------------------------------------------------------------------
// Yield
// ---------------------------------------------------------------------------
/**
 * Base per-second yield of one hex, before worker coverage is applied.
 *
 * `special` is added *after* the building multiplier rather than before, so a
 * building can never scale Aether. Nothing in the MVP building set could, but
 * getting the order wrong here would quietly make some future sawmill-equivalent
 * a way to multiply the one resource the design wants to stay scarce.
 */
export function hexYield(biome, building, special) {
    const result = cloneBundle(BIOME_YIELDS[biome]);
    if (building !== null) {
        const multipliers = BUILDINGS[building].yieldMultiplier;
        for (const type of RESOURCE_TYPES) {
            const multiplier = multipliers[type];
            if (multiplier !== undefined)
                result[type] *= multiplier;
        }
    }
    if (special !== null) {
        const bonus = SPECIAL_HEX_YIELDS[special];
        for (const type of RESOURCE_TYPES)
            result[type] += bonus[type];
    }
    return result;
}
/**
 * Combined yield multiplier from every world event acting on a hex.
 *
 * Multiplicative rather than "worst wins": two disasters on one hex should hurt
 * more than one, and a hex under both a drought and an eruption is genuinely in a
 * worse place than either alone.
 */
export function eventYieldScale(scales) {
    const result = {
        food: 1,
        timber: 1,
        ore: 1,
        gold: 1,
        aether: 1,
    };
    for (const scale of scales) {
        for (const type of RESOURCE_TYPES) {
            const factor = scale[type];
            if (factor !== undefined)
                result[type] *= factor;
        }
    }
    return result;
}
/**
 * How much of its land a kingdom is actually working, 0..1.
 *
 * This is the coupling that makes expansion a decision rather than a reflex.
 * Claiming a hex you have no workers for dilutes the whole kingdom's output, so
 * "grab everything" is a real strategic error and not just a slow start.
 */
export function workerEfficiency(workers, territoryCount) {
    if (territoryCount <= 0)
        return 0;
    const needed = territoryCount * WORKERS_PER_HEX;
    if (needed <= 0)
        return MAX_WORKER_EFFICIENCY;
    return Math.min(MAX_WORKER_EFFICIENCY, workers / needed);
}
export function populationCap(foodYieldPerSecond) {
    return BASE_POPULATION_CAP + foodYieldPerSecond * POPULATION_PER_FOOD_RATE;
}
export function workerCount(population, workerRatio) {
    return population * clamp01(workerRatio);
}
export function troopCount(population, workerRatio) {
    return population * (1 - clamp01(workerRatio));
}
// ---------------------------------------------------------------------------
// Expansion
// ---------------------------------------------------------------------------
/**
 * Cost to claim one hex, scaled by how much land the claimant already holds.
 *
 * Open question #4: the scaling curve. Linear-in-territory was chosen over
 * exponential because it is legible — a player can feel "my next hex costs about
 * twice what my first did" in a way they cannot feel a power curve.
 */
export function expansionCost(biome, territoryCount) {
    const base = EXPANSION_COSTS[biome];
    const multiplier = 1 + Math.max(0, territoryCount) * EXPANSION_SCALING_PER_HEX;
    return roundBundle(scaleBundle(base, multiplier));
}
/**
 * Whether a kingdom may claim a hex, and what it costs.
 *
 * Claiming is for *unowned* land only. Taking territory from another kingdom is
 * a combat outcome, resolved by border pressure — you cannot buy a hex out from
 * under someone.
 */
export function checkClaim(target, context) {
    const cost = context.biome === null ? emptyBundle() : expansionCost(context.biome, context.claimantTerritoryCount);
    const reject = (reason) => ({ allowed: false, reason, cost });
    if (context.claimantEliminated)
        return reject("eliminated");
    if (context.biome === null)
        return reject("off-map");
    if (context.owner !== null)
        return reject("already-owned");
    const adjacent = neighbors(target).some((n) => context.neighbourOwners.get(hexKey(n)) === context.claimantId);
    if (!adjacent)
        return reject("not-adjacent");
    if (!canAfford(context.claimantResources, cost))
        return reject("insufficient-resources");
    return { allowed: true, reason: null, cost };
}
export function checkBuild(buildingType, context) {
    const definition = BUILDINGS[buildingType];
    const cost = cloneBundle(definition.cost);
    const reject = (reason) => ({ allowed: false, reason, cost });
    if (context.biome === null)
        return reject("off-map");
    if (context.owner !== context.builderId)
        return reject("not-owned");
    if (context.existingBuilding !== null)
        return reject("already-built");
    if (!definition.allowedBiomes.includes(context.biome))
        return reject("wrong-terrain");
    if (!canAfford(context.builderResources, cost))
        return reject("insufficient-resources");
    return { allowed: true, reason: null, cost };
}
export function buildableBiomes(buildingType) {
    return BUILDINGS[buildingType].allowedBiomes;
}
/**
 * This hex's defence multiplier from its building. `1` when there is none.
 *
 * An accessor rather than a direct `BUILDINGS[b].defenceMultiplier` at each call
 * site, because the null-hex case is the common one and every caller would
 * otherwise repeat the same guard — which is how one of them ends up forgetting.
 */
export function buildingDefenceMultiplier(building) {
    return building === null ? 1 : BUILDINGS[building].defenceMultiplier;
}
/** This hex's contribution to its owner's storage cap. `0` when there is none. */
export function buildingStorageBonus(building) {
    return building === null ? 0 : BUILDINGS[building].storageBonus;
}
/**
 * Whether a god tool may be used on a hex, and what it costs.
 *
 * The ownership rule differs by tool on purpose, and it is the whole character of
 * the set: `terraform` and `fortify` improve ground you hold, so they require it.
 * `calm` ends something happening to you, so it also requires it. `provoke` calls
 * a disaster down on someone *else*, so it requires the opposite — you cannot
 * provoke your own land, which would otherwise be a way to farm fertile ash from
 * an eruption you scheduled.
 */
export function checkGodTool(tool, context) {
    const cost = cloneBundle(GOD_TOOLS_DEFINITIONS[tool].cost);
    const reject = (reason) => ({ allowed: false, reason, cost });
    if (context.biome === null)
        return reject("off-map");
    if (tool === "provoke") {
        // Anywhere that is not yours: enemy land or neutral ground both work.
        if (context.owner === context.userId)
            return reject("not-owned");
        if (context.underEvent)
            return reject("already-disrupted");
    }
    else {
        if (context.owner !== context.userId)
            return reject("not-owned");
    }
    if (tool === "calm" && !context.underEvent)
        return reject("nothing-to-calm");
    if (tool === "fortify" && context.fortified)
        return reject("already-fortified");
    if (!canAfford(context.userResources, cost))
        return reject("insufficient-resources");
    return { allowed: true, reason: null, cost };
}
/**
 * The defender's pressure on one hex.
 *
 * Terrain and fortification multiply rather than add, so walls are worth most on
 * ground that already defends well. That is the intended shape — it makes a
 * mountain line with walls a genuine strategic asset instead of making every hex
 * equally defensible — but it is also the sharpest number in the game: it sits
 * directly on the attack/defence ratio that `SURGE_WEIGHT` exists to keep above
 * 1.0, so changing it means re-running `tools/diag.ts`.
 */
export function defencePressure(context) {
    if (context.defenderId === null)
        return 0;
    const terrain = TERRAIN_DEFENCE[context.biome] *
        buildingDefenceMultiplier(context.building) *
        (context.fortified ? FORTIFY_DEFENCE_MULTIPLIER : 1);
    return (context.defenderTroopsOnHex * terrain +
        context.defenderAdjacentTroops * ADJACENT_SUPPORT_FACTOR * terrain);
}
export function resolveCombat(context) {
    const defence = defencePressure(context);
    let contender = null;
    let attack = 0;
    for (const attacker of context.attackers) {
        if (attacker.kingdomId === context.defenderId)
            continue;
        if (attacker.troops > attack) {
            attack = attacker.troops;
            contender = attacker.kingdomId;
        }
    }
    if (contender === null || attack <= 0) {
        return {
            contender: null,
            attackPressure: 0,
            defencePressure: defence,
            netPressure: 0,
            captureDelta: -CAPTURE_DECAY_RATE,
            attritionPerSide: 0,
        };
    }
    const net = attack - defence;
    const attrition = Math.min(attack, defence) * ATTRITION_RATE;
    return {
        contender,
        attackPressure: attack,
        defencePressure: defence,
        netPressure: net,
        captureDelta: net > 0 ? net * CAPTURE_RATE : -CAPTURE_DECAY_RATE,
        attritionPerSide: attrition,
    };
}
/**
 * Target share of a kingdom's troops for one frontier hex.
 *
 * Surged segments pull a larger share, and troops move toward their target
 * gradually rather than teleporting — the surge has to travel, which is what
 * makes committing to a push a decision with a cost rather than a free button.
 */
export function troopTargetWeight(surgeCount) {
    return 1 + Math.max(0, surgeCount) * (SURGE_WEIGHT - 1);
}
/**
 * docs/01: Glory Score is the reason a kingdom that builds and trades well can
 * win without ever fighting — something pure OpenFront doesn't offer. Territory
 * counts, but not so heavily that it collapses back into "who conquered most".
 */
export function gloryScore(input) {
    const territory = input.territoryCount * GLORY_PER_HEX;
    // Special hexes score under `buildings` rather than as a fifth term: docs/04
    // fixes the four fields of `GloryScore` and the end screen itemises exactly
    // those, so adding a term would change the wire format and the panel for
    // something that is conceptually the same thing — a fixed-value asset you hold
    // on a hex. Worth revisiting if the trade phase reworks the breakdown anyway.
    const buildings = input.buildings.reduce((sum, type) => sum + BUILDINGS[type].gloryValue, 0) +
        Math.max(0, input.specialHexes) * GLORY_PER_SPECIAL_HEX;
    const banked = bundleTotal(input.banked) * GLORY_PER_BANKED_RESOURCE;
    const tradeVolume = input.tradeVolume * GLORY_PER_TRADE_VOLUME;
    return {
        kingdomId: input.kingdomId,
        territory: round2(territory),
        buildings: round2(buildings),
        banked: round2(banked),
        tradeVolume: round2(tradeVolume),
        total: round2(territory + buildings + banked + tradeVolume),
    };
}
/** Standings, highest total first. Ties break on territory, then kingdom id. */
export function rankStandings(scores) {
    return [...scores].sort((a, b) => {
        if (b.total !== a.total)
            return b.total - a.total;
        if (b.territory !== a.territory)
            return b.territory - a.territory;
        return a.kingdomId.localeCompare(b.kingdomId);
    });
}
// ---------------------------------------------------------------------------
// Border segments
// ---------------------------------------------------------------------------
/**
 * A border segment is one edge: the owned hex plus the direction index of the
 * neighbour being pushed into.
 *
 * Open question #2 asked whether segments should be contiguous *runs* of edges
 * instead. Per-edge wins because ids stay valid when territory changes shape —
 * a run-based id can be invalidated mid-drag by a hex flipping owner, which is
 * exactly when the player is most likely to be issuing surge orders.
 */
export function borderSegmentId(owned, direction) {
    return `${hexKey(owned)}:${direction}`;
}
export function parseBorderSegmentId(id) {
    const colon = id.lastIndexOf(":");
    if (colon === -1)
        throw new Error(`Malformed border segment id: ${JSON.stringify(id)}`);
    const hexPart = id.slice(0, colon);
    const comma = hexPart.indexOf(",");
    if (comma === -1)
        throw new Error(`Malformed border segment id: ${JSON.stringify(id)}`);
    const q = Number(hexPart.slice(0, comma));
    const r = Number(hexPart.slice(comma + 1));
    const direction = Number(id.slice(colon + 1));
    if (!Number.isFinite(q) || !Number.isFinite(r) || !Number.isInteger(direction)) {
        throw new Error(`Malformed border segment id: ${JSON.stringify(id)}`);
    }
    if (direction < 0 || direction > 5) {
        throw new Error(`Border segment direction out of range: ${direction}`);
    }
    return { hex: { q, r }, direction };
}
// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
export function clamp01(value) {
    if (Number.isNaN(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function round2(value) {
    return Math.round(value * 100) / 100;
}
function roundBundle(bundle) {
    return {
        food: Math.ceil(bundle.food),
        timber: Math.ceil(bundle.timber),
        ore: Math.ceil(bundle.ore),
        gold: Math.ceil(bundle.gold),
        aether: Math.ceil(bundle.aether),
    };
}
//# sourceMappingURL=rules.js.map